import { Handshake, Landmark, TrendingUp, Users, ArrowRight, Check } from "lucide-react";

const INK = "#111111";
const YELLOW = "#FFD60A";
const AMBER = "#8A6D00";

const PHASES = [
  {
    Icon: Handshake, tag: "Phase 1 · Close the deal",
    t: "Compliance & legal to get the partnership signed",
    points: [
      "Program agreement negotiation — we've negotiated several bank program agreements",
      "Diligence responses drafted and organized, not scrambled",
      "Day-1 policies, risk assessment, and BSA/AML program the bank expects to see",
    ],
  },
  {
    Icon: Landmark, tag: "Phase 2 · Keep the bank happy",
    t: "The ongoing relationship, run like clockwork",
    points: [
      "Bank reporting packages, monitoring & testing on their calendar",
      "Complaint handling, marketing review, and training with a live audit trail",
      "Unusual activity reporting that feeds their suspicious activity reporting",
      "Annual diligence refreshes and bank audits — an export, not a project",
    ],
  },
  {
    Icon: TrendingUp, tag: "Phase 3 · Grow & scale",
    t: "A program that scales with the company",
    points: [
      "New products and states → risk assessment and policies refreshed, not rebuilt",
      "Fractional hours flex up during tax season, down after",
      "Hire your own team when ready — we train and hand off cleanly — or keep the fractional team",
    ],
  },
];

const GUIDE = [
  {
    n: "1", t: "Start with the risk assessment",
    b: "For a brand-new company, this is the map — we inventory everything you'll need against what exists today and hand you a prioritized build list with owners and dates.",
  },
  {
    n: "2", t: "Stand up the recommended team",
    b: "Fractional CCO, BSA/AML officer, and a compliance analyst from day one — senior coverage at a fraction of three full-time hires.",
  },
  {
    n: "3", t: "Grow your way",
    b: "Hire your own team as you scale — we train them and hand off cleanly — or keep the fractional team permanently. Either way, the platform and the record stay with you.",
  },
];

export function DueDiligenceGuide() {
  return (
    <section id="diligence" className="anchor-target py-14 md:py-20" data-testid="diligence-section">
      <span className="af-eyebrow">Compliance &amp; legal · from term sheet to scale</span>
      <h2 className="af-h text-4xl md:text-5xl mt-3 max-w-3xl" data-testid="diligence-headline">
        Close the deal. Keep the bank happy. <span className="af-grad-text">Grow without the growing pains.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        A bank partnership isn't a one-time approval — it's a relationship your compliance program
        has to earn every quarter. We handle the compliance and legal work at every stage: getting
        the deal signed, keeping your bank partner confident, and scaling the program as
        Accountable grows.
      </p>

      <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PHASES.map(({ Icon, tag, t, points }, i) => (
          <div key={tag} className="af-card p-6 flex flex-col" data-testid={`phase-card-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${YELLOW}33` }}>
                <Icon className="h-5 w-5" style={{ color: AMBER }} strokeWidth={1.8} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-[0.14em]" style={{ color: AMBER }}>{tag}</span>
            </div>
            <div className="af-h text-lg mb-3" style={{ color: INK }}>{t}</div>
            <ul className="space-y-2 flex-1">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-[13.5px] leading-relaxed" style={{ color: INK, opacity: 0.75 }}>
                  <Check className="h-3.5 w-3.5 mt-1 flex-shrink-0" style={{ color: AMBER }} strokeWidth={3} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* The guide for a brand-new company */}
      <div className="mt-10 af-card p-6 md:p-8" style={{ borderColor: `${AMBER}40` }} data-testid="diligence-guide">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-4 w-4" style={{ color: AMBER }} />
          <span className="af-eyebrow">The guide · for a brand-new company</span>
        </div>
        <h3 className="af-h text-2xl md:text-3xl mt-2 max-w-2xl" style={{ color: INK }}>
          Risk assessment first. Fractional team next. Then grow — your way.
        </h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {GUIDE.map(({ n, t, b }) => (
            <div key={n} className="rounded-2xl p-5 relative" style={{ background: `${YELLOW}14`, border: `1px solid ${YELLOW}66` }} data-testid={`guide-step-${n}`}>
              <div className="absolute top-3 right-4 af-h text-4xl" style={{ color: `${AMBER}30` }}>{n}</div>
              <div className="af-h text-base mb-1.5 pr-8" style={{ color: INK }}>{t}</div>
              <p className="text-[13px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
            </div>
          ))}
        </div>
        <a href="#build" className="af-cta-pink inline-flex items-center gap-2 h-11 px-6 text-sm mt-6">
          Start with the risk assessment <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
